import { useI18n } from "@core/i18n/i18n";
import { formatDayColumn, getDayColumnIndexes } from "@domain/program/program";
import { DayIndex } from "@domain/program/program-days";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { Tooltip } from "@ui/components/common/tooltip";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { DialogProps } from "primereact/dialog";
import { classNames } from "primereact/utils";
import React, { useCallback, useMemo, useState } from "react";

export interface DuplicateDayColumnDialogProps extends DialogProps {
	onConfirm?: (sourceSessionDayIndex: DayIndex, destinationSessionDayIndexes: DayIndex[]) => void;
	initialDayColumns: Map<DayIndex, DayColumnState>;
}

export enum DayColumnState {
	SOURCE = "SOURCE",
	AVAILABLE = "AVAILABLE",
	NOT_AVAILABLE = "NOT_AVAILABLE",
	SELECTED = "SELECTED",
}

const stateEdges: Record<DayColumnState, DayColumnState> = {
	AVAILABLE: DayColumnState.SELECTED,
	SELECTED: DayColumnState.AVAILABLE,
	NOT_AVAILABLE: DayColumnState.NOT_AVAILABLE,
	SOURCE: DayColumnState.SOURCE,
};

export const DuplicateDayColumnDialog = ({
	onConfirm,
	initialDayColumns,
	...otherProps
}: DuplicateDayColumnDialogProps): JSX.Element => {
	const { format } = useI18n();

	const sourceSessionIndex = useMemo(
		() =>
			[...initialDayColumns.entries()].find(([, dayColumnState]) => dayColumnState === DayColumnState.SOURCE)?.[0] ??
			DayIndex.MONDAY,
		[initialDayColumns],
	);

	const [dayColumns, setDayColumns] = useState(initialDayColumns);

	const handleDayClicked = useCallback(
		(clickedDayIndex: DayIndex) =>
			setDayColumns(current => {
				const newDayColumns = new Map([...current.entries()]);
				newDayColumns.set(clickedDayIndex, stateEdges[newDayColumns.get(clickedDayIndex) ?? DayColumnState.AVAILABLE]);
				return newDayColumns;
			}),
		[],
	);

	const submitSelection = useCallback(() => {
		const destinationSessionIndexes = [...dayColumns.entries()]
			.filter(([, dayColumnState]) => dayColumnState === DayColumnState.SELECTED)
			.map(([dayIndex]) => dayIndex);
		onConfirm?.(sourceSessionIndex, destinationSessionIndexes);
		otherProps.onHide();
	}, [dayColumns, onConfirm, sourceSessionIndex, otherProps]);

	return (
		<BaseDialog
			header={format("programBuilder.dayTable.duplicateDayColumnDialog.header", {
				name: formatDayColumn(sourceSessionIndex, "long"),
			})}
			footer={
				<ButtonContainer>
					<Button
						label={format("programBuilder.dayTable.duplicateDayColumnDialog.confirmButton")}
						disabled={![...dayColumns.values()].some(day => day === DayColumnState.SELECTED)}
						onClick={submitSelection}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			<DialogContainer>
				<Header>
					{format("programBuilder.dayTable.duplicateDayColumnDialog.description", {
						name: formatDayColumn(sourceSessionIndex, "long"),
					})}
					<DaysContainer>
						{getDayColumnIndexes().map(columnIndex => {
							const dayState = dayColumns.get(columnIndex) ?? DayColumnState.AVAILABLE;
							return (
								<Tooltip
									key={columnIndex}
									content={format("programBuilder.dayTable.duplicateDayColumnDialog.tooltip.unavailableDay")}
									placement="bottom"
									disabled={dayState !== DayColumnState.NOT_AVAILABLE}
								>
									<Day
										state={dayState}
										className={classNames("p-size-l-bold", {
											"unavailable-day": dayState === DayColumnState.NOT_AVAILABLE,
										})}
										onClick={() => {
											if (dayColumnStatesAttributes[dayState].selectable) handleDayClicked(columnIndex);
										}}
									>
										{formatDayColumn(columnIndex, "min")}
									</Day>
								</Tooltip>
							);
						})}
					</DaysContainer>
				</Header>
			</DialogContainer>
		</BaseDialog>
	);
};

interface DayColumnStateColors {
	backgroundColor: string;
	fontColor: string;
	borderColor: string;
}

interface DayColumnStateAttributes {
	normal: DayColumnStateColors;
	hover?: DayColumnStateColors;
	selectable: boolean;
}

const dayColumnStatesAttributes: Record<DayColumnState, DayColumnStateAttributes> = {
	AVAILABLE: {
		normal: { backgroundColor: colors.white, borderColor: colors.grey50, fontColor: colors.grey },
		hover: { backgroundColor: colors.white, borderColor: colors.primary, fontColor: colors.primary },
		selectable: true,
	},
	NOT_AVAILABLE: {
		normal: { backgroundColor: colors.white, borderColor: colors.white, fontColor: colors.grey80 },
		selectable: false,
	},
	SOURCE: {
		normal: { backgroundColor: colors.primary, borderColor: colors.primary, fontColor: colors.white },
		selectable: false,
	},
	SELECTED: {
		normal: { backgroundColor: colors.primary25, borderColor: colors.primary, fontColor: colors.primary },
		selectable: true,
	},
};

const DialogContainer = styled.div`
	height: 100%;
	${stack("M", "spacebetween", "stretch")}
`;

const Header = styled.div``;

const ButtonContainer = styled.div`
	${row("S", "flexend")}
`;

const DaysContainer = styled.div`
	display: grid;
	grid-auto-flow: column;
	gap: 8px;
	margin-top: 24px;
	margin-bottom: 32px;
`;

const Day = styled.div<{ state: DayColumnState }>`
	border-radius: 2px;
	border-style: solid;
	border-width: 2px;
	background-color: ${({ state }) => dayColumnStatesAttributes[state].normal.backgroundColor};
	border-color: ${({ state }) => dayColumnStatesAttributes[state].normal.borderColor};
	color: ${({ state }) => dayColumnStatesAttributes[state].normal.fontColor};
	cursor: ${({ state }) => (dayColumnStatesAttributes[state].selectable ? "pointer" : undefined)};
	text-align: center;
	padding: 8px;

	:hover {
		background-color: ${({ state }) => dayColumnStatesAttributes[state].hover?.backgroundColor};
		border-color: ${({ state }) => dayColumnStatesAttributes[state].hover?.borderColor};
		color: ${({ state }) => dayColumnStatesAttributes[state].hover?.fontColor};
	}
`;
